/**
 * Focus Service
 * Handles focus-related operations for settings
 */

import { supabase } from '../lib/supabase';

// Log default schema configuration
if (import.meta.env.DEV) {
  console.log('📋 [FocusService] Default Supabase schema: deepsearch (explicitly using schema("public") for focuses)');
}

class FocusService {
  /**
   * Get all focuses for a user
   */
  async getUserFocuses(userId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('focuses')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching focuses:', error);
      throw error;
    }
  }

  /**
   * Get a single focus by ID
   */
  async getFocusById(focusId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('focuses')
        .select('*')
        .eq('id', focusId)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching focus by ID:', error);
      return { data: null, error };
    }
  }

  /**
   * Create a new focus
   */
  async createFocus(userId, focusData) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('focuses')
        .insert([{
          user_id: userId,
          ...focusData
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating focus:', error);
      throw error;
    }
  }

  /**
   * Update a focus
   */
  async updateFocus(focusId, updates) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('focuses')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', focusId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating focus:', error);
      throw error;
    }
  }

  /**
   * Rename a focus
   */
  async renameFocus(focusId, newTitle) {
    try {
      return await this.updateFocus(focusId, { title: newTitle });
    } catch (error) {
      console.error('Error renaming focus:', error);
      throw error;
    }
  }

  /**
   * Delete a focus
   */
  async deleteFocus(focusId) {
    try {
      const { error } = await supabase
        .schema('public')
        .from('focuses')
        .delete()
        .eq('id', focusId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting focus:', error);
      throw error;
    }
  }

  /**
   * Set primary focus for a user
   */
  async setPrimaryFocus(userId, focusId) {
    try {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'focusService.js:137',message:'setPrimaryFocus entry',data:{userId,focusId},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      const { data, error } = await supabase
        .schema('public')
        .from('user_profiles')
        .update({
          primary_focus_id: focusId,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'focusService.js:148',message:'setPrimaryFocus result',data:{hasError:!!error,errorCode:error?.code},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error setting primary focus:', error);
      throw error;
    }
  }

  /**
   * Toggle auto-save conversations for a focus
   */
  async toggleAutoSaveConversations(focusId, enabled) {
    try {
      return await this.updateFocus(focusId, { auto_save_conversations: enabled });
    } catch (error) {
      console.error('Error toggling auto-save conversations:', error);
      throw error;
    }
  }

  /**
   * Update focus last_active_at (call when user opens focus page)
   */
  async updateLastActive(focusId) {
    try {
      const { error } = await supabase.rpc('update_focus_last_active', {
        focus_id_param: focusId
      });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error updating focus last_active_at:', error);
      throw error;
    }
  }

  /**
   * Archive a focus (sets status to 'archived')
   */
  async archiveFocus(focusId) {
    try {
      return await this.updateFocus(focusId, { status: 'archived' });
    } catch (error) {
      console.error('Error archiving focus:', error);
      throw error;
    }
  }

  /**
   * Upload focus cover image
   */
  async uploadCoverImage(focusId, file) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${focusId}-${Date.now()}.${fileExt}`;
      const filePath = `focus-covers/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('user_uploads')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('user_uploads')
        .getPublicUrl(filePath);

      await this.updateFocus(focusId, { cover_image_url: publicUrl });

      return publicUrl;
    } catch (error) {
      console.error('Error uploading cover image:', error);
      throw error;
    }
  }

  // ==========================================
  // FOCUS LIBRARY METHODS (focus_items join table)
  // ==========================================

  /**
   * Get library items for a focus
   * Two-step fetch: focus_items -> deepsearch.items
   * @param {string} focusId - Focus UUID
   * @param {number} limit - Max items to return
   * @param {number} offset - Pagination offset
   * @returns {Promise<{data: Array, error: any}>}
   */
  async getFocusLibraryItems(focusId, limit = 20, offset = 0) {
    try {
      // Step 1: Get item IDs from focus_items (explicitly use public schema)
      const { data: focusItems, error: focusError } = await supabase
        .schema('public')
        .from('focus_items')
        .select('item_id, created_at')
        .eq('focus_id', focusId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (focusError) throw focusError;
      
      if (!focusItems || focusItems.length === 0) {
        return { data: [], error: null };
      }

      // Step 2: Fetch items from deepsearch.items
      const itemIds = focusItems.map(fi => fi.item_id);
      const addedAtMap = {};
      focusItems.forEach(fi => {
        addedAtMap[fi.item_id] = fi.created_at;
      });

      const { data: items, error: itemsError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select(`
          id,
          title,
          description,
          url,
          image_url,
          source_type,
          source_table,
          thumbnail_url,
          created_at,
          auto_tags,
          meta
        `)
        .in('id', itemIds);

      if (itemsError) throw itemsError;

      // Add added_at timestamp and sort by it
      const itemsWithAddedAt = (items || []).map(item => ({
        ...item,
        added_at: addedAtMap[item.id]
      }));

      // Sort by added_at descending (most recent first)
      itemsWithAddedAt.sort((a, b) => 
        new Date(b.added_at) - new Date(a.added_at)
      );

      console.log(`✅ [getFocusLibraryItems] Loaded ${itemsWithAddedAt.length} items for focus ${focusId}`);
      return { data: itemsWithAddedAt, error: null };

    } catch (error) {
      console.error('❌ [getFocusLibraryItems] Error:', error);
      console.error('❌ [getFocusLibraryItems] Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      return { data: null, error };
    }
  }

  /**
   * Get focus memberships for multiple items
   * Returns which focuses each item belongs to
   * Two-step fetch to ensure explicit schema usage
   * @param {string[]} itemIds - Array of item UUIDs
   * @returns {Promise<{data: Object, error: any}>} Map of itemId -> [{focus_id, focus_title}]
   */
  async getFocusMembershipsForItems(itemIds) {
    if (!itemIds?.length) return { data: {}, error: null };

    try {
      // Step 1: Get focus_items from public schema
      const { data: focusItems, error: focusItemsError } = await supabase
        .schema('public')
        .from('focus_items')
        .select('item_id, focus_id')
        .in('item_id', itemIds);

      if (focusItemsError) throw focusItemsError;

      if (!focusItems || focusItems.length === 0) {
        return { data: {}, error: null };
      }

      // Step 2: Get unique focus IDs and fetch focuses from public schema
      const uniqueFocusIds = [...new Set(focusItems.map(fi => fi.focus_id))];
      
      const { data: focuses, error: focusesError } = await supabase
        .schema('public')
        .from('focuses')
        .select('id, title')
        .in('id', uniqueFocusIds);

      if (focusesError) throw focusesError;

      // Step 3: Build focus lookup map
      const focusMap = {};
      (focuses || []).forEach(f => {
        focusMap[f.id] = f.title;
      });

      // Step 4: Merge in JS - build map of itemId -> array of {focus_id, focus_title}
      const map = {};
      for (const row of focusItems) {
        if (!map[row.item_id]) map[row.item_id] = [];
        if (focusMap[row.focus_id]) {
          map[row.item_id].push({
            focus_id: row.focus_id,
            focus_title: focusMap[row.focus_id]
          });
        }
      }

      console.log(`✅ [getFocusMembershipsForItems] Loaded memberships for ${itemIds.length} items (two-step fetch)`);
      return { data: map, error: null };

    } catch (e) {
      console.error('❌ [getFocusMembershipsForItems] Error:', e);
      return { data: null, error: e };
    }
  }
}

export default new FocusService();

